import {
  AlertColor,
  autocompleteClasses,
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { Formik, FormikProps } from 'formik';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
  FC,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AddressForm from '../components/formlib/AddressForm';
import AutocompleteSearch from '../components/formlib/AutocompleteSearch';
import ButtonComponent from '../components/formlib/ButtonComponent';
import DatePickerComponent from '../components/formlib/DatePickerComponent';
import DropdownComponent from '../components/formlib/DropdownComponent';
import MediumTypography from '../components/formlib/MediumTypography';
import SelectComponent from '../components/formlib/SelectComponent';
import Textinput from '../components/formlib/Textinput';
import TooltipTableCell from '../components/formlib/TooltipTableCell';
import {
  REFERRAL_STATUS_KEY,
  CLIENT_ID_KEY,
  EXCLUDING_DATES,
} from '../services/Constant';
import {
  AddNewParent,
  ContactsList,
  DateString,
  FaceSheetDemographicsType,
  IDResponse,
  InsuranceDetails,
  OptionType,
} from '../utils/type';
import { Gender } from './Dropdown';
import { HeadCell, TableHeader } from '../components/formlib/TableHeader';
import {
  getMasterInsuranceListAll,
  InsuranceType,
} from '../services/configApi/insurance/insuranceProvider';
import { ReactComponent as EditIcon } from '../assets/images/editvector.svg';
import * as yup from 'yup';
import { datePickerMinDateMaxDateValidate } from '../utils/dateUtil';
import { ReferralResponseType } from '../services/configApi/Clients';
import {
  convertGenderToBackend,
  convertGenderToFrontend,
} from '../utils/genderUtil';
import { useMultiplePermissions } from '../hooks/usePermissions';
import { LookUpContext } from '../context/LookUpContextProvider';
import {
  addClientInsurance,
  getClientInsurances,
  getSubscriberList,
  updateClientInsurance,
} from '../services/configApi/User';
import { LoaderContext, LoaderContextType } from '../layouts/AppSidebar';
import { ApiError, isCustomError } from '../services/ApiResponseHandler';
import { UnSavedChangesContext } from '../context/UnSavedChangesProvider';
import SnackBarComponent from '../components/formlib/SnackBarComponent';
import ModalPopup from '../components/formlib/ModalPopup';
import './Insurance.css';
import _ from 'lodash';

interface Data {
  name: string;
  type: string;
  Subscriber: string;
  tableEffectiveStartdate: string;
  effectivedate: string;
  status: string;
  actions: string;
}

const insuranceValues: InsuranceDetails = {
  id: null,
  dph3rdPartyPayer: null,
  insuranceCompany: null,
  dphReasonCode: null,
  dphAddendum: null,
  masterInsuranceId: null,
  dph3rdPartyPayerName: null,
  coverageType2: null,
  subscriber: {
    firstName: null,
    lastName: null,
    middleName: null,
    address1: null,
    address2: null,
    city: { id: 'asd', label: '' },
    zipCode: null,
    state: null,
    phone: '',
    dateOfBirth: null,
    sex: null,
    subscriberMemberId: null,
    patientRelationToInsured: null,
    subscriberType: '',
    subscriberGroupNumber: null,
    subscriberPlan: null,
  },
  effectiveStartDate: null,
  effectiveEndDate: null,
  isEdit: false,
  updated: false,
  status: '',
};

const headCells: HeadCell<Data>[] = [
  {
    id: 'name',
    labelId: 'Insurance.Name',
    defaultLabelId: 'Insurance Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'type',
    labelId: 'Insurance.Type',
    defaultLabelId: 'Type',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'Subscriber',
    labelId: 'Insurance.Subscriber',
    defaultLabelId: 'Subscriber',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'tableEffectiveStartdate',
    labelId: 'insurance.effectiveStartDate',
    defaultLabelId: 'Effective Start Date',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'effectivedate',
    labelId: 'Insurance.Effectivedate',
    defaultLabelId: 'Effective End Date',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'status',
    labelId: 'Dashboard.Status',
    defaultLabelId: 'Status',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'actions',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
];

const coverageTypes: OptionType[] = [
  {
    id: 'Primary',
    label: 'Primary',
  },
  {
    id: 'Secondary',
    label: 'Secondary',
  },
  {
    id: 'Tertiary',
    label: 'Tertiary',
  },
];

const validationSchema = yup.object().shape({
  dph3rdPartyPayer: yup
    .string()
    .nullable()
    .required('insurance.dph3rdPartyPayerRequiredText'),
  insuranceCompany: yup.string().when(['dph3rdPartyPayer'], {
    is: 'OTHER',
    then: yup.string().required('insurance.companyrequired'),
    otherwise: yup.string().nullable(),
  }),
  dphReasonCode: yup.string().when(['dph3rdPartyPayer'], {
    is: '00',
    then: yup.string().required('insurance.dphReasonCodeReq'),
    otherwise: yup.string().nullable(),
  }),
  dphAddendum: yup.string().nullable(),
  coverageType2: yup.string().required('coverageTypeRequired').nullable(),
  subscriber: yup.object().shape({
    firstName: yup.string().required('Clientpage.First.validation'),
    lastName: yup.string().required('Clientpage.last.validation'),
    middleName: yup.string().nullable(),
    dateOfBirth: yup.date().required('parent.dateofbirthMessage').nullable(),
    sex: yup.string().required('Clientpage.Gender.validation').nullable(),
    subscriberMemberId: yup
      .string()
      .required('insurance.subscriberMemberIdReq')
      .max(300, 'subscriberMemberIdValidation')
      .nullable(),
    patientRelationToInsured: yup
      .string()
      .required('insurance.clientRelationtoInsuredReq')
      .nullable(),
    phone: yup
      .string()
      .min(12, 'PhoneNumber.Validation')
      .required('ContactOthers.phonenumberMandatory')
      .nullable(),
    subscriberGroupNumber: yup
      .string()
      .nullable()
      .max(40, 'subscriberGroupNumberValidation'),
    subscriberPlan: yup.string().nullable().max(40, 'subscriberPlanValidation'),
    address1: yup.string().required('addressLine1RequiredMessage').nullable(),
    address2: yup.string().nullable(),
    zipCode: yup
      .string()
      .min(5, 'validZipCodeErrorMessage')
      .required('zipCodeRequiredMessage'),
    city: yup.object().shape({
      label: yup.string().required('cityRequiredMessage'),
    }),
    state: yup.string().required('stateRequiredMessage').nullable(),
  }),
  effectiveStartDate: yup
    .date()
    .required('insurance.effectiveStartDateReq')
    .nullable(),
  effectiveEndDate: yup.date().nullable(),
});

interface TrackFields {
  values: InsuranceDetails;
  program: OptionType | null;
  subscriber: string | null;
  initialValues: InsuranceDetails;
  insuranceAddendum: OptionType | null;
}

const FormObserver: FC<TrackFields> = ({
  values,
  program,
  subscriber,
  initialValues,
  insuranceAddendum,
}) => {
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  useEffect(() => {
    const hasFormChanges = !_.isEqual(values, initialValues);

    // Check if dropdown selections represent actual changes from initial values
    const isProgramChanged =
      program !== null &&
      program.label !== '' &&
      values.masterInsuranceId !== initialValues.masterInsuranceId;

    const isAddendumChanged =
      insuranceAddendum !== null &&
      insuranceAddendum.label !== '' &&
      values.dphAddendum !== initialValues.dphAddendum;

    const isSubscriberChanged = subscriber !== null && subscriber !== '';
    const hasDropdownChanges =
      isProgramChanged || isSubscriberChanged || isAddendumChanged;
    const hasChanges = hasFormChanges || hasDropdownChanges;
    handleUpdateUnsavedChanges(hasChanges);
  }, [
    values,
    initialValues,
    handleUpdateUnsavedChanges,
    program,
    subscriber,
    insuranceAddendum,
  ]);
  return null;
};

const ClientInsurance = () => {
  const rctl = useIntl();
  const [openModal, setOpenModal] = useState(false);
  const { lookups } = useContext(LookUpContext);
  const [insurances, setInsurances] = useState<InsuranceDetails[]>([]);
  const formRef = useRef<FormikProps<InsuranceDetails>>(null);
  const [selectedInsuranceAddendum, setSelectedInsuranceAddendum] =
    useState<OptionType | null>(null);
  const [subscribers, setSubscribers] = useState<AddNewParent[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<OptionType | null>(
    null,
  );
  const [insuranceData, setInsuranceData] = useState<InsuranceDetails>(
    structuredClone(insuranceValues),
  );
  const [activeInsurances, setActiveInsurances] = useState<OptionType[]>([]);
  const [activeMasterInsurance, setActiveMasterInsurance] = useState<
    InsuranceType[]
  >([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<OptionType[]>(
    [],
  );
  const [selectedSubscriber, setSelectedSubscriber] = useState<string | null>(
    null,
  );
  const [lookupDphInsuranceAddendum, setLookupDphInsuranceAddendum] = useState<
    OptionType[]
  >([]);
  const [lookupDphReasonCode, setLookupDphReasonCode] = useState<OptionType[]>(
    [],
  );
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const [lookupPatientRelation, setLookupPatientRelation] = useState<
    OptionType[]
  >([]);
  const [showAlertDialog, setShowAlertDialog] = useState<boolean>(false);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [open, setOpen] = useState(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [action, setAction] = useState<'ADD' | 'UPDATE' | 'DELETE'>('ADD');
  const showToastr = useCallback(
    (type: AlertColor, id: string, message: string) => {
      setOpen(true);
      setToastrVariable(type);
      setToastrId(id);
      setToastrDefaultMessage(message);
    },
    [],
  );

  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;

  useEffect(() => {
    if (!hasEditPermission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission]);

  useEffect(() => {
    handleUpdateUnsavedChanges(false);
    toggleLoader(true);
    getMasterInsuranceList();
    getClientInsuranceList();
  }, []);

  useEffect(() => {
    const clientId = localStorage.getItem(CLIENT_ID_KEY);
    if (clientId) {
      getSubscriberList(clientId)
        .then((response) => {
          const clientSubscriber = getClientDetailForSubscriber();
          if (clientSubscriber) {
            response.push(clientSubscriber);
          }
          setSubscribers(response);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (lookups) {
      setLookupDphReasonCode(() => {
        return lookups.dphInsuranceReasonCode.map((item) => {
          return {
            id: item.code,
            label: item.code.concat(` - `).concat(item.description),
          };
        });
      });
      setLookupDphInsuranceAddendum(() => {
        return lookups.eicsInsuranceAddendum.map((item) => {
          return { id: item.code, label: item.description };
        });
      });
      setLookupPatientRelation(() => {
        return lookups.eicsPatientRelation.map((item) => {
          return { id: item.code, label: item.description };
        });
      });
    }
  }, [lookups]);

  const nameValidation = (value: string): string | undefined => {
    const trimmedValue = value?.replace(/^\s+/, '');
    const regex = /^[A-Za-z0-9,'. -]*$/;
    if (regex.test(trimmedValue)) {
      return trimmedValue;
    }
  };

  const getInsuranceName = (id: string) => {
    const foundInsuranceName = insuranceCompanies.find((val) => {
      return val.id === id;
    })?.label;
    return foundInsuranceName ?? '';
  };

  const getInsuranceAddendumName = (id: string) => {
    const foundInsuranceAddendumName = lookupDphInsuranceAddendum.find(
      (val) => {
        return val.id === id;
      },
    )?.label;
    return foundInsuranceAddendumName ?? '';
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const getClientInsuranceList = () => {
    const clientId: string = localStorage.getItem('ClientId') ?? '';
    getClientInsurances(clientId)
      .then((response: InsuranceDetails[]) => {
        let updatedValue: InsuranceDetails[] = [];
        updatedValue = response.filter((item) => {
          item.status = assignInsuranceStatus(
            item.effectiveStartDate,
            item.effectiveEndDate,
          );

          if (EXCLUDING_DATES.includes(item.effectiveEndDate as string)) {
            item.effectiveEndDate = null;
          }
          if (EXCLUDING_DATES.includes(item.effectiveStartDate as string)) {
            item.effectiveStartDate = null;
          }
          return item;
        });

        setInsurances(updatedValue);
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setInsurances([]);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else if (error.code === 1111) {
          setToastrId('overlapInsuranceMessage');
          setToastrDefaultMessage(
            'The same Coverage Types should not have overlapping dates.',
          );
        } else if (error.code === 1112) {
          setToastrId('activeInsuranceValidation');
          setToastrDefaultMessage(
            'Active insurance exists for the client. Make them In-active to add DPH',
          );
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
        }
      });
  };

  const getClientDetailForSubscriber = (): AddNewParent | null => {
    const clientId: string = localStorage.getItem('ClientId') ?? '';
    const clientInfo = localStorage.getItem('Demographics');
    if (clientInfo !== null) {
      const client = JSON.parse(clientInfo) as FaceSheetDemographicsType;
      const clientReferralDetails = JSON.parse(
        localStorage.getItem('clientReferralDetails') as unknown as string,
      ) as ReferralResponseType[];
      const defaultReferralId = localStorage.getItem('defaultReferralId');
      if (
        clientReferralDetails &&
        clientReferralDetails.length > 0 &&
        defaultReferralId
      )
        clientReferralDetails.forEach((referral: ReferralResponseType) => {
          if (referral.referralId === defaultReferralId) {
            client.dphId = referral.dphId;
          }
        });
      const value: AddNewParent = {
        firstName: client.firstName ?? '',
        lastName: client.lastName ?? '',
        relationShipToClient: '',
        sex: client.sex ?? '',
        isAddressSameAsClientAddress: false,
        email: '',
        dateOfBirth: client.dateOfBirth,
        isPrimary: false,
        isLegal: client.isLegal ?? false,
        homePhoneNumber: '',
        workPhoneNumber: '',
        mobilePhoneNumber: '',
        street: client.address.street ?? '',
        apartment: client.address.address2 ?? '',
        city: client.address.city ?? '',
        zipCode: client.address.zipCode ?? '',
        state: client.address.state ?? '',
        isParentIndex: false,
        careGiverId: clientId,
        isEdit: false,
        contactId: client.dphId ?? '',
      };
      return value;
    }
    return null;
  };

  const getMasterInsuranceList = () => {
    getMasterInsuranceListAll()
      .then((response: InsuranceType[]) => {
        const InsuranceCList: OptionType[] = [];
        const activeList: OptionType[] = [];
        const newActiveInsurances: InsuranceType[] = [];
        response?.forEach((item) => {
          const name = `${item.payerId} - ${item.insuranceCompanyName}`;
          InsuranceCList.push({
            id: item.insuranceId ?? '',
            label: name,
          });
          const endDate = dayjs(item.endDate, 'MM/DD/YYYY');
          const today = dayjs();
          const isSameOrAfterToday =
            endDate.isSame(today, 'day') || endDate.isAfter(today, 'day');
          let insuranceName = name;
          if (item.endDate !== null && !isSameOrAfterToday) {
            insuranceName += ' (Not Honored)';
          }
          if (!item.isDeleted) {
            activeList.push({
              id: item.insuranceId ?? '',
              label: insuranceName,
            });
          }
          newActiveInsurances.push(item);
        });

        setInsuranceCompanies(InsuranceCList);
        setActiveMasterInsurance(newActiveInsurances);
        setActiveInsurances(activeList);
        toggleLoader(false);
      })
      .catch((error) => {
        setToastrVariable('error');
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
        }
      });
  };

  const assignInsuranceStatus = (
    startDate: DateString,
    endDate: DateString,
  ) => {
    if (startDate && dayjs().isBefore(dayjs(startDate))) {
      return rctl.formatMessage({
        id: 'notStartedText',
        defaultMessage: 'Not Started',
      });
    }
    if (endDate === null || dayjs().isSame(dayjs(startDate))) {
      return rctl.formatMessage({
        id: 'activeText',
        defaultMessage: 'Active',
      });
    }
    const effectiveEndDate = dayjs(endDate);
    if (effectiveEndDate.isBefore(dayjs(), 'date')) {
      return rctl.formatMessage({
        id: 'inactiveText',
        defaultMessage: 'Inactive',
      });
    }
    return rctl.formatMessage({
      id: 'activeText',
      defaultMessage: 'Active',
    });
  };

  const NumValidation = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    const formattedValue = cleanValue.replace(
      /(\d{3})(\d{0,3})(\d{0,4})/,
      (match, p1, p2, p3) => {
        let formatted = p1;
        if (p2) formatted += '-' + p2;
        if (p3) formatted += '-' + p3;
        return formatted;
      },
    );
    return formattedValue.slice(0, 12);
  };

  const getSubscriberPhoneNumber = (
    subscriber: AddNewParent,
  ): string | undefined => {
    if (subscriber.mobilePhoneNumber) {
      return subscriber.mobilePhoneNumber;
    } else if (subscriber.workPhoneNumber) {
      return subscriber.workPhoneNumber;
    } else if (subscriber.homePhoneNumber) {
      return subscriber.homePhoneNumber;
    }
  };

  const setSubscriberSelection = (id: string) => {
    const values = structuredClone(formRef.current?.values);
    if (values) {
      const subscriber = subscribers.find((item) => item.careGiverId === id);
      if (subscriber) {
        values.subscriber.firstName = subscriber.firstName;
        values.subscriber.lastName = subscriber.lastName;
        values.subscriber.middleName = null;
        values.subscriber.dateOfBirth = subscriber.dateOfBirth;
        values.subscriber.sex = subscriber.sex
          ? convertGenderToFrontend(subscriber.sex)
          : '';
        values.subscriber.phone = getSubscriberPhoneNumber(subscriber) ?? '';
        values.subscriber.city = {
          id: 'sda',
          label: subscriber.city,
        } as OptionType;
        values.subscriber.address1 = subscriber.street;
        values.subscriber.address2 = subscriber.apartment;
        values.subscriber.zipCode = subscriber.zipCode;
        values.subscriber.state = subscriber.state;
        values.subscriber.subscriberType =
          subscriber.careGiverId.length === 5 ? 'client' : '';
        values.subscriber.patientRelationToInsured =
          subscriber.careGiverId.length === 5 ? '18' : '';
        if (values.dph3rdPartyPayer === '00') {
          values.subscriber.subscriberMemberId =
            subscriber.careGiverId.length === 5 ? subscriber.contactId : '';
        } else {
          values.subscriber.subscriberMemberId = null;
        }
      } else {
        values.subscriber.firstName = null;
        values.subscriber.lastName = null;
        values.subscriber.middleName = null;
        values.subscriber.dateOfBirth = null;
        values.subscriber.sex = null;
        values.subscriber.patientRelationToInsured = null;
        values.subscriber.address1 = null;
        values.subscriber.address2 = null;
        values.subscriber.city = { id: 'asd', label: '' };
        values.subscriber.zipCode = null;
        values.subscriber.state = null;
        values.subscriber.phone = '';
        values.subscriber.subscriberType = '';
        values.subscriber.subscriberMemberId = null;
      }
      formRef.current?.setValues(values);
    }
    setSelectedSubscriber(id);
  };

  const getRelationshipValue = (code: string) => {
    if (code && lookups) {
      const guardianType = lookups.guardianType.find(
        (type) => type.code === code,
      );
      if (guardianType) {
        return '-' + ' ' + guardianType.description;
      }
    }
    return '-' + ' ' + 'Client';
  };

  const getClientDetail = (): ContactsList | null => {
    const clientId: string = localStorage.getItem('ClientId') ?? '';
    const clientInfo = localStorage.getItem('Demographics');
    if (clientInfo !== null) {
      const client = JSON.parse(clientInfo) as FaceSheetDemographicsType;
      const clientReferralDetails = JSON.parse(
        localStorage.getItem('clientReferralDetails') as unknown as string,
      ) as ReferralResponseType[];
      const defaultReferralId = localStorage.getItem('defaultReferralId');
      if (
        clientReferralDetails &&
        clientReferralDetails.length > 0 &&
        defaultReferralId
      )
        clientReferralDetails.forEach((referral: ReferralResponseType) => {
          if (referral.referralId === defaultReferralId) {
            client.dphId = referral.dphId;
          }
        });
      const value: ContactsList = {
        id: clientId,
        firstName: client.firstName,
        lastName: client.lastName,
        middleName: client.middleName,
        type: 'Client',
        isPrimary: false,
        DOB: client.dateOfBirth,
        sex: convertGenderToFrontend(client.sex ?? '') ?? '',
        homeNumber: '',
        isLegal: client.isLegal ?? false,
        contactType: 'client',
        enrollmentId: client.dphId,
      };
      return value;
    }
    return null;
  };

  const alphaNumericValidation = (value: string) => {
    let trimmedValue = value.replace(/^\s+/, '');
    const maxLength = 100;

    if (trimmedValue.length > maxLength) {
      trimmedValue = trimmedValue.slice(0, maxLength);
    }

    const regex = /^[A-Za-z0-9.,!' ]*$/;
    if (regex.test(trimmedValue)) {
      return trimmedValue;
    }
  };

  const getActiveInActiveInsuranceName = (payerId: string) => {
    const insurance = activeMasterInsurance.find(
      (val) => val.payerId === payerId,
    );
    if (insurance) {
      const name = `${insurance.payerId} - ${insurance.insuranceCompanyName}`;
      const endDate = dayjs(insurance.endDate, 'MM/DD/YYYY');
      const today = dayjs();
      const isSameOrAfterToday =
        endDate.isSame(today, 'day') || endDate.isAfter(today, 'day');
      let insuranceName = name;
      if (insurance.endDate !== null && !isSameOrAfterToday) {
        insuranceName += ' (Not Honored)';
      }
      return insuranceName;
    }
    return '-';
  };

  const getCoverageType = (coverageType: string | null) => {
    if (coverageType === null) {
      return '-';
    }
    const value = coverageTypes.find((type) => type.id === coverageType);
    return value?.label ?? '-';
  };

  const displayCoverageType = (coverageType: string) => {
    if (
      coverageType === 'primary' ||
      coverageType === 'secondary' ||
      coverageType === 'tertiary'
    ) {
      return getCoverageType(coverageType);
    }
    return coverageType;
  };

  const validateForm = (value: InsuranceDetails) => {
    const errors: Partial<InsuranceDetails> = {};
    if (value.effectiveEndDate) {
      if (
        dayjs(value.effectiveEndDate).isBefore(dayjs(value.effectiveStartDate))
      ) {
        errors.effectiveEndDate = 'effectiveEndDateValidationMessage';
      }
    }
    if (datePickerMinDateMaxDateValidate(value.effectiveStartDate)) {
      errors.effectiveStartDate = 'datePickerMinDateMaxDateValidate';
    }

    if (datePickerMinDateMaxDateValidate(value.effectiveEndDate)) {
      errors.effectiveEndDate = 'datePickerMinDateMaxDateValidate';
    }

    if (datePickerMinDateMaxDateValidate(value.subscriber.dateOfBirth)) {
      errors.subscriber = {
        subscriberGroupNumber: null,
        subscriberPlan: null,
      };
      errors.subscriber.dateOfBirth = 'datePickerMinDateMaxDateValidate';
    }
    return errors;
  };

  const clearForm = () => {
    const newValues = structuredClone(insuranceValues);
    formRef.current?.resetForm({ values: newValues });
    setInsuranceData(newValues);
    setSelectedProgram(null);
    setSelectedInsuranceAddendum(null);
    setSelectedSubscriber(null);
    setOpenModal(false);
    handleUpdateUnsavedChanges(false);
    setAction('ADD');
  };

  const createInsurance = (insurance: InsuranceDetails) => {
    insurance.subscriber.city = (insurance.subscriber.city as OptionType).label;
    insurance.subscriber.sex = insurance.subscriber.sex
      ? convertGenderToBackend(insurance.subscriber.sex)
      : null;
    const clientId: string = localStorage.getItem('ClientId') ?? '';

    toggleLoader(true);
    addClientInsurance(clientId, insurance)
      .then((response: IDResponse) => {
        insurance.id = response.id;
        insurance.status = assignInsuranceStatus(
          insurance.effectiveStartDate,
          insurance.effectiveEndDate,
        );
        setInsurances((prevInsurances) => [...prevInsurances, insurance]);
        clearForm();
        setOpen(true);
        toggleLoader(false);
        setToastrVariable('success');
        setToastrId('Insurance.createsuccess');
        setToastrDefaultMessage('Insurance Added Successfully');
      })
      .catch((error) => {
        setOpen(true);
        toggleLoader(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else if (error.code === 1110) {
          setToastrId('noStartDateErrorMessage');
          setToastrDefaultMessage('Effective start date should not be empty');
        } else {
          setToastrId('Insurance.createFailureApiMsg');
          setToastrDefaultMessage('Failed to add Insurance');
        }
      });
  };

  const handleUpdateInsurance = (insurance: InsuranceDetails) => {
    setInsurances((previous) => {
      return previous.map((i) => (i.id === insurance.id ? insurance : i));
    });
  };

  const updateInsurance = (insurance: InsuranceDetails) => {
    // Ensure we have a valid ID for updating
    if (!insurance.id) {
      showToastr('error', 'updateError', 'Cannot update insurance: Missing ID');
      return;
    }

    insurance.subscriber.city = (insurance.subscriber.city as OptionType).label;
    insurance.subscriber.sex = insurance.subscriber.sex
      ? convertGenderToBackend(insurance.subscriber.sex)
      : null;
    const clientId: string = localStorage.getItem('ClientId') ?? '';

    toggleLoader(true);
    updateClientInsurance(clientId, insurance)
      .then(() => {
        insurance.status = assignInsuranceStatus(
          insurance.effectiveStartDate,
          insurance.effectiveEndDate,
        );

        if (EXCLUDING_DATES.includes(insurance.effectiveEndDate as string)) {
          insurance.effectiveEndDate = null;
        }
        if (EXCLUDING_DATES.includes(insurance.effectiveStartDate as string)) {
          insurance.effectiveStartDate = null;
        }

        handleUpdateInsurance(insurance);
        clearForm();
        setOpen(true);
        toggleLoader(false);
        setToastrVariable('success');
        setToastrId('Insurance.updatesuccess');
        setToastrDefaultMessage('Insurance Updated Successfully');
      })
      .catch((error) => {
        setOpen(true);
        toggleLoader(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else if (error.code === 1110) {
          setToastrId('noStartDateErrorMessage');
          setToastrDefaultMessage('Effective start date should not be empty');
        } else {
          setToastrId('Insurance.updateFailureApiMsg');
          setToastrDefaultMessage('Failed to update Insurance');
        }
      });
  };

  const formatDate = (date: string | Dayjs | null) => {
    return date === null ? null : dayjs(date).format('MM/DD/YYYY');
  };

  return (
    <Box component="div">
      <Box>
        <SnackBarComponent
          open={open}
          handleClose={handleClose}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
        <ModalPopup
          open={showAlertDialog}
          description="insurance.medicaidPopup"
          onOk={() => setShowAlertDialog(false)}
          labelId2="Clientpage.Okbtn"
          positiveActionLabel="deleteText"
        />
        <ModalPopup
          open={openModal}
          description="formUnsavedChangesMessage"
          onCancel={() => setOpenModal(false)}
          onOk={() => clearForm()}
          labelId1="Clientpage.Nobtn"
          negativeActionLabel="No"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
        <Box
          component="section"
          style={{
            pointerEvents: hasEditPermission ? 'auto' : 'none',
            opacity: hasEditPermission ? 'unset' : '0.8',
          }}
        >
          <TableContainer sx={{ maxHeight: 310 }} className="tableStyles">
            <Table stickyHeader aria-label="sticky table">
              <TableHeader
                className="listDataTableHead"
                headerNames={headCells}
                checkBoxRequired={false}
              />

              <TableBody className="tableRowcss">
                {insurances.length === 0 && ( // Check if insurances is empty
                  <TableRow>
                    <TableCell colSpan={6} align="center" id="nodata">
                      <FormattedMessage
                        id="Insurance.nodataValidation"
                        defaultMessage="No Insurance added yet"
                      />
                    </TableCell>
                  </TableRow>
                )}
                {insurances.length > 0 &&
                  insurances.map((row: InsuranceDetails, index: number) => (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.id ?? `insurance-${index}`}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor:
                          index % 2 === 0 ? '#ECF9F8' : '#ffffff',
                      }}
                    >
                      <TooltipTableCell
                        value={getActiveInActiveInsuranceName(
                          row.dph3rdPartyPayer ?? '',
                        )}
                        textSxProps={{
                          maxWidth: 200,
                        }}
                        visible={true}
                      />
                      <TooltipTableCell
                        value={
                          row.coverageType2 === null
                            ? '-'
                            : displayCoverageType(row.coverageType2)
                        }
                        visible={true}
                      />
                      <TooltipTableCell
                        value={`${row.subscriber.lastName ?? ''}, ${
                          row.subscriber.firstName ?? ''
                        } ${row.subscriber.middleName ?? ''}`}
                        visible={true}
                      />
                      <TableCell>
                        {row.effectiveStartDate
                          ? ` ${row.effectiveStartDate} `
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {row.effectiveEndDate
                          ? ` ${row.effectiveEndDate} `
                          : '-'}
                      </TableCell>

                      <TableCell>
                        <Box className={'flex__ alignItemCenter'}>
                          <MediumTypography
                            label={row.status}
                            className="mr-xs"
                          />
                          {/* <Tooltip
                              id="tool-tip"
                              title={rctl.formatMessage({
                                id: 'notVerifiedText',
                                defaultMessage: 'Not Verified',
                              })}
                              placement="right"
                            >
                              <StatusCheckIcon
                                fill="var(--thom-mono-gray)"
                                style={{ cursor: 'pointer' }}
                              />
                            </Tooltip> */}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box className="flex__ alignItemCenter cursorPointer">
                          <Box>
                            <Tooltip title="Edit">
                              <EditIcon
                                onClick={() => {
                                  const insurance = structuredClone(row);
                                  insurance.effectiveStartDate = formatDate(
                                    insurance.effectiveStartDate,
                                  );
                                  insurance.effectiveEndDate = formatDate(
                                    insurance.effectiveEndDate,
                                  );
                                  insurance.subscriber = {
                                    ...insurance.subscriber,
                                    sex: insurance.subscriber.sex
                                      ? convertGenderToFrontend(
                                          insurance.subscriber.sex,
                                        )
                                      : '',
                                    dateOfBirth: insurance.subscriber
                                      .dateOfBirth
                                      ? formatDate(
                                          insurance.subscriber.dateOfBirth,
                                        )
                                      : null,
                                    subscriberType:
                                      insurance.dph3rdPartyPayer === '00'
                                        ? 'client'
                                        : '',
                                    city: {
                                      id: 'asd',
                                      label: insurance.subscriber
                                        .city as string,
                                    },
                                  };
                                  setSelectedProgram({
                                    id: '',
                                    label:
                                      insurance.masterInsuranceId === null
                                        ? ''
                                        : getInsuranceName(
                                            insurance.masterInsuranceId,
                                          ),
                                  });
                                  setSelectedInsuranceAddendum({
                                    id: '',
                                    label:
                                      insurance.dphAddendum === null
                                        ? ''
                                        : getInsuranceAddendumName(
                                            insurance.dphAddendum,
                                          ),
                                  });
                                  formRef.current?.setValues(insurance);
                                  setSelectedSubscriber(null);
                                  setInsuranceData({ ...insurance });
                                  setAction('UPDATE');
                                }}
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box>
            <Box className="mt-sm">
              <Card>
                <Formik
                  initialValues={insuranceData}
                  validationSchema={validationSchema}
                  validate={validateForm}
                  innerRef={formRef}
                  validateOnBlur={false}
                  validateOnChange={true}
                  onSubmit={(values) => {
                    const newValues = structuredClone(values);
                    if (action === 'ADD') {
                      createInsurance(newValues);
                    } else if (action === 'UPDATE') {
                      updateInsurance(newValues);
                    }
                  }}
                >
                  {({
                    initialValues,
                    setFieldValue,
                    errors,
                    touched,
                    values,
                  }) => (
                    <>
                      <FormObserver
                        values={values}
                        program={selectedProgram}
                        subscriber={selectedSubscriber}
                        initialValues={initialValues}
                        insuranceAddendum={selectedInsuranceAddendum}
                      />
                      <Box sx={{ padding: '16px 16px 0px 16px' }}>
                        <Grid
                          container
                          rowSpacing={'24px'}
                          columnSpacing={'40px'}
                        >
                          <Grid item xs={12} lg={8}>
                            <MediumTypography
                              labelid="Insurance.details"
                              defaultlabel="Insurance Details"
                              sxProps={{
                                color: '#2A4241',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                marginRight: '16px',
                              }}
                            />
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <AutocompleteSearch
                              labelId={'insurance.dph3rdPartyPayer'}
                              defaultId={'No Search found'}
                              Required
                              data={activeInsurances}
                              onTextChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => {
                                if (event.target.value.length === 0) {
                                  formRef.current?.resetForm();
                                }
                              }}
                              onSelectItem={(value: OptionType | null) => {
                                if (value !== null) {
                                  const newValues = { ...values };
                                  setSelectedProgram(value);
                                  newValues.masterInsuranceId = value.id;
                                  const selectInsurancePdph3rdPartyPayer =
                                    activeMasterInsurance.find(
                                      (item) => item.insuranceId === value.id,
                                    );
                                  if (selectInsurancePdph3rdPartyPayer) {
                                    if (
                                      selectInsurancePdph3rdPartyPayer.payerId ===
                                      '00'
                                    ) {
                                      const client = getClientDetail();
                                      if (client) {
                                        const dateofBirth: string | null =
                                          client.DOB
                                            ? dayjs(client.DOB).format(
                                                'MM/DD/YYYY',
                                              )
                                            : null;
                                        newValues.subscriber.firstName =
                                          client.firstName;
                                        newValues.subscriber.lastName =
                                          client.lastName;
                                        newValues.subscriber.middleName =
                                          client.middleName;
                                        newValues.subscriber.subscriberMemberId =
                                          client.enrollmentId;
                                        newValues.subscriber.sex = client.sex;
                                        newValues.subscriber.dateOfBirth =
                                          dateofBirth;
                                        newValues.subscriber.subscriberType =
                                          client.contactType;
                                        newValues.subscriber.phone =
                                          client.homeNumber
                                            ? client.homeNumber
                                            : '';
                                        newValues.subscriber.patientRelationToInsured =
                                          '18';
                                        const clientFound = subscribers.find(
                                          (item) =>
                                            item.careGiverId === client.id,
                                        );
                                        if (clientFound) {
                                          newValues.subscriber.address1 =
                                            clientFound.street;
                                          newValues.subscriber.address2 =
                                            clientFound.apartment;
                                          newValues.subscriber.zipCode =
                                            clientFound.zipCode;
                                          newValues.subscriber.state =
                                            clientFound.state;
                                          newValues.subscriber.city = {
                                            id: 'asd',
                                            label: clientFound.city as string,
                                          };
                                        }
                                      } else {
                                        newValues.subscriber.firstName = null;
                                        newValues.subscriber.lastName = null;
                                        newValues.subscriber.middleName = null;
                                        newValues.subscriber.subscriberMemberId =
                                          null;
                                        newValues.subscriber.sex = null;
                                        newValues.subscriber.dateOfBirth = null;
                                        newValues.subscriber.subscriberType =
                                          '';
                                        newValues.subscriber.phone = '';
                                        newValues.subscriber.patientRelationToInsured =
                                          '';
                                        newValues.subscriber.address1 = null;
                                        newValues.subscriber.address2 = null;
                                        newValues.subscriber.zipCode = null;
                                        newValues.subscriber.state = null;
                                        newValues.subscriber.city = {
                                          id: 'asd',
                                          label: '',
                                        };
                                      }
                                      newValues.coverageType2 = 'Primary';
                                      newValues.effectiveEndDate = null;
                                    } else if (action !== 'UPDATE') {
                                      newValues.coverageType2 = null;
                                      newValues.dphAddendum = null;
                                      newValues.dphReasonCode = null;
                                      newValues.effectiveStartDate = null;
                                      newValues.effectiveEndDate = null;
                                      newValues.subscriber.firstName = null;
                                      newValues.subscriber.lastName = null;
                                      newValues.subscriber.middleName = null;
                                      newValues.subscriber.subscriberMemberId =
                                        null;
                                      newValues.subscriber.sex = null;
                                      newValues.subscriber.dateOfBirth = null;
                                      newValues.subscriber.subscriberType = '';
                                      newValues.subscriber.phone = '';
                                      newValues.subscriber.patientRelationToInsured =
                                        '';
                                      newValues.subscriber.address1 = null;
                                      newValues.subscriber.address2 = null;
                                      newValues.subscriber.zipCode = null;
                                      newValues.subscriber.state = null;
                                      newValues.subscriber.city = {
                                        id: 'asd',
                                        label: '',
                                      };
                                    }
                                    newValues.dph3rdPartyPayerName =
                                      selectInsurancePdph3rdPartyPayer.insuranceCompanyName;
                                    newValues.dph3rdPartyPayer =
                                      selectInsurancePdph3rdPartyPayer.payerId;
                                    formRef.current?.setValues(newValues);
                                    setSelectedSubscriber(null);
                                  }

                                  setFieldValue(`dphReasonCode`, null);
                                  setFieldValue('dphAddendum', null);
                                  setSelectedInsuranceAddendum(null);
                                }
                              }}
                              disableClearable={true}
                              disableLabel={true}
                              optionHintId={'No Search found'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                },
                              }}
                              selectedValue={selectedProgram as OptionType}
                            />
                            {errors.dph3rdPartyPayer &&
                              touched.dph3rdPartyPayer && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.dph3rdPartyPayer}
                                  defaultlabel="3rd Party Payer is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <DropdownComponent
                              names={coverageTypes}
                              name={'coverageType2'}
                              labelid="Insurance.coverageType"
                              defaultlabelid="Coverage Type"
                              value={values.coverageType2 ?? ''}
                              handleChange={(value: string) => {
                                setFieldValue(`coverageType2`, value);
                              }}
                              disabled={!hasEditPermission}
                              Required
                            />

                            {errors.coverageType2 && touched.coverageType2 && (
                              <MediumTypography
                                labelid={errors.coverageType2}
                                defaultlabel="Coverage Type is required"
                                sxProps={{ color: 'red' }}
                              />
                            )}
                          </Grid>
                          {values.dph3rdPartyPayer === 'OTHER' && (
                            <Grid item xs={4.5} lg={4.5}>
                              <Textinput
                                name={'insuranceCompany'}
                                labelid="insuranceCompanyText"
                                defaultlabelid="Insurance Company"
                                handlechange={(value: string) => {
                                  const inputValue = value;
                                  const validatedValue =
                                    alphaNumericValidation(inputValue);
                                  if (validatedValue !== undefined) {
                                    setFieldValue(
                                      'insuranceCompany',
                                      validatedValue,
                                    );
                                  }
                                }}
                                Required={values.dph3rdPartyPayer === 'OTHER'}
                                Value={values.insuranceCompany}
                                inputProps={{
                                  maxLength: 50,
                                }}
                              />
                              {errors.insuranceCompany &&
                                touched.insuranceCompany && (
                                  <MediumTypography
                                    sxProps={{ color: 'red' }}
                                    labelid={errors.insuranceCompany}
                                    defaultlabel="Insurance Company is required"
                                  />
                                )}
                            </Grid>
                          )}
                          <Grid item xs={4.5} lg={4.5}>
                            <DropdownComponent
                              names={lookupDphReasonCode}
                              name={'DphReasonCode'}
                              labelid="Insurance.DphReasonCode"
                              defaultlabelid="DPH Reason Code"
                              value={values.dphReasonCode ?? ''}
                              handleChange={(value: string) => {
                                setFieldValue(`dphReasonCode`, value);
                              }}
                              disabled={
                                values.dph3rdPartyPayer !== '00' ||
                                !hasEditPermission
                              }
                              Required={values.dph3rdPartyPayer === '00'}
                            />

                            {errors.dphReasonCode && touched.dphReasonCode && (
                              <MediumTypography
                                labelid={errors.dphReasonCode}
                                defaultlabel="DPH Reason Code is required"
                                sxProps={{ color: 'red' }}
                              />
                            )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <AutocompleteSearch
                              labelId={'Insurance.DphInsuranceAddendum'}
                              defaultId={'DPH Insurance Addendum'}
                              data={lookupDphInsuranceAddendum}
                              onSelectItem={(value: OptionType | null) => {
                                if (value === null) {
                                  setFieldValue('dphAddendum', '');
                                  setSelectedInsuranceAddendum(null);
                                } else {
                                  setFieldValue('dphAddendum', value.id);
                                  setSelectedInsuranceAddendum(value);
                                }
                              }}
                              disableLabel={true}
                              optionHintId={'No Search found'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                },
                              }}
                              selectedValue={
                                selectedInsuranceAddendum as OptionType
                              }
                              disabled={values.dph3rdPartyPayer === '00'}
                            />
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <DatePickerComponent
                              required={true}
                              labelid="insurance.effectiveStartDate"
                              defaultlabelid="Effective Start Date"
                              handlechange={(date: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                if (date === null) {
                                  setFieldValue('effectiveStartDate', null);
                                  setFieldValue('effectiveEndDate', null);
                                } else {
                                  if (values.effectiveEndDate) {
                                    const endDate = dayjs(
                                      values.effectiveEndDate,
                                    );
                                    if (
                                      endDate.isBefore(date) ||
                                      endDate.isSame(date)
                                    ) {
                                      setFieldValue('effectiveEndDate', null);
                                    }
                                  }
                                  setFieldValue(
                                    'effectiveStartDate',
                                    formattedDate,
                                  );
                                }
                              }}
                              value={
                                values.effectiveStartDate === null
                                  ? null
                                  : dayjs(values.effectiveStartDate)
                              }
                            />
                            {errors.effectiveStartDate &&
                              touched.effectiveStartDate && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.effectiveStartDate}
                                  defaultlabel="Effective Start Date is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5}>
                            <DatePickerComponent
                              labelid="insurance.effectiveEndDate"
                              minDate={
                                values.effectiveStartDate
                                  ? dayjs(values.effectiveStartDate).add(
                                      1,
                                      'day',
                                    )
                                  : undefined
                              }
                              defaultlabelid="Effective End Date"
                              handlechange={(date: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                if (date === null) {
                                  setFieldValue('effectiveEndDate', null);
                                } else {
                                  setFieldValue(
                                    'effectiveEndDate',
                                    formattedDate,
                                  );
                                }
                              }}
                              value={
                                values.effectiveEndDate === null
                                  ? null
                                  : dayjs(values.effectiveEndDate)
                              }
                            />
                            {errors.effectiveEndDate &&
                              touched.effectiveEndDate && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.effectiveEndDate}
                                  defaultlabel="Effective End Date is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={12} lg={8}>
                            <MediumTypography
                              labelid="subscriberInformationText"
                              defaultlabel="Subscriber Information"
                              sxProps={{
                                color: '#2A4241',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                marginRight: '16px',
                              }}
                            />
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <DropdownComponent
                              labelid="searchSubscriberText"
                              defaultlabelid="Select Subscriber"
                              disabled={
                                values.dph3rdPartyPayer === '00' ||
                                !hasEditPermission
                              }
                              names={subscribers.map((item) => {
                                const prefixes = [
                                  item.isPrimary && 'Primary',
                                  item.isLegal && 'Legal',
                                ].filter(Boolean);
                                return {
                                  id: item.careGiverId,
                                  label: `${item.lastName ?? ''}, ${
                                    item.firstName ?? ''
                                  } ${getRelationshipValue(
                                    item.relationShipToClient,
                                  )} ${
                                    prefixes.length > 0
                                      ? ` - (${prefixes.join(', ')})`
                                      : ''
                                  }`,
                                } as OptionType;
                              })}
                              value={selectedSubscriber ?? ''}
                              handleChange={(id: string) => {
                                setSubscriberSelection(id);
                              }}
                            />
                          </Grid>
                          <Grid item xs={4.5} lg={4.5} />
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={'subscriberFirstName'}
                              labelid="Clientpage.First"
                              defaultlabelid="First Name"
                              handlechange={(value: string) => {
                                const validatedValue = nameValidation(value);
                                if (validatedValue !== undefined) {
                                  setFieldValue(
                                    'subscriber.firstName',
                                    validatedValue,
                                  );
                                }
                              }}
                              Required
                              Value={values.subscriber.firstName ?? ''}
                              inputProps={{
                                maxLength: 40,
                              }}
                            />
                            {errors.subscriber?.firstName &&
                              touched.subscriber?.firstName && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.subscriber.firstName}
                                  defaultlabel="First Name is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={'subscriberMiddleName'}
                              labelid="Clientpage.Middle"
                              defaultlabelid="Middle Name"
                              handlechange={(value: string) => {
                                const validatedValue = nameValidation(value);
                                if (validatedValue !== undefined) {
                                  setFieldValue(
                                    'subscriber.middleName',
                                    validatedValue,
                                  );
                                }
                              }}
                              Value={values.subscriber.middleName ?? ''}
                              inputProps={{
                                maxLength: 40,
                              }}
                            />
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={'subscriberLastName'}
                              labelid="Clientpage.Last"
                              defaultlabelid="Last Name"
                              handlechange={(value: string) => {
                                const validatedValue = nameValidation(value);
                                if (validatedValue !== undefined) {
                                  setFieldValue(
                                    'subscriber.lastName',
                                    validatedValue,
                                  );
                                }
                              }}
                              Required
                              Value={values.subscriber.lastName ?? ''}
                              inputProps={{
                                maxLength: 40,
                              }}
                            />
                            {errors.subscriber?.lastName &&
                              touched.subscriber?.lastName && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.subscriber.lastName}
                                  defaultlabel="Last Name is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={'subscriberMemberId'}
                              labelid="insurance.subscriberMemberIdText"
                              defaultlabelid="Subscriber Member ID"
                              handlechange={(value: string) => {
                                const validatedValue = nameValidation(value);
                                if (validatedValue !== undefined) {
                                  setFieldValue(
                                    'subscriber.subscriberMemberId',
                                    validatedValue,
                                  );
                                }
                              }}
                              Required
                              Value={values.subscriber.subscriberMemberId ?? ''}
                              inputProps={{
                                maxLength: 300,
                              }}
                            />
                            {errors.subscriber?.subscriberMemberId &&
                              touched.subscriber?.subscriberMemberId && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.subscriber.subscriberMemberId}
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <DropdownComponent
                              names={lookupPatientRelation}
                              name={'patientRelationToInsured'}
                              labelid="Insurance.ClientRelationtoInsured"
                              defaultlabelid="Client Relationship to the Subscriber"
                              value={
                                values.subscriber.patientRelationToInsured ?? ''
                              }
                              handleChange={(value: string) => {
                                setFieldValue(
                                  `subscriber.patientRelationToInsured`,
                                  value,
                                );
                              }}
                              disabled={
                                values.subscriber.subscriberType === 'client' ||
                                !hasEditPermission
                              }
                              Required
                            />

                            {errors.subscriber?.patientRelationToInsured &&
                              touched.subscriber?.patientRelationToInsured && (
                                <MediumTypography
                                  labelid={
                                    errors.subscriber.patientRelationToInsured
                                  }
                                  defaultlabel="Subscriber Relation to Insured is required"
                                  className="errorText-md"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <DatePickerComponent
                              disableFuture={true}
                              labelid="insurance.subscriberDateOfBirth"
                              defaultlabelid="Subscriber Date of Birth"
                              handlechange={(date: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                if (date === null) {
                                  setFieldValue('subscriber.dateOfBirth', null);
                                } else {
                                  setFieldValue(
                                    'subscriber.dateOfBirth',
                                    formattedDate,
                                  );
                                }
                              }}
                              required
                              value={
                                values.subscriber.dateOfBirth === null
                                  ? null
                                  : dayjs(values.subscriber.dateOfBirth)
                              }
                            />
                            {errors.subscriber?.dateOfBirth &&
                              touched.subscriber?.dateOfBirth && (
                                <MediumTypography
                                  labelid={errors.subscriber.dateOfBirth}
                                  defaultlabel="Date of Birth is required"
                                  className="errorText-md"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <SelectComponent
                              labelid="Clientpage.Sex"
                              defaultlabelid="sex"
                              name="sex"
                              Required
                              value={values.subscriber.sex ?? ''}
                              handleChange={(value: string) => {
                                setFieldValue('subscriber.sex', value);
                              }}
                              names={Gender}
                            />
                            {errors.subscriber?.sex &&
                              touched.subscriber?.sex && (
                                <MediumTypography
                                  labelid={errors.subscriber.sex}
                                  defaultlabel="Sex is required"
                                  className="errorText-md"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={`homePhoneNumber`}
                              labelid="Contacts.phone"
                              defaultlabelid="Phone Number"
                              handlechange={(value: string) => {
                                setFieldValue(
                                  'subscriber.phone',
                                  NumValidation(value),
                                );
                              }}
                              inputProps={{
                                pattren: ['0-9'],
                                maxLength: 12,
                              }}
                              Required
                              Value={values.subscriber.phone}
                            />
                            {errors.subscriber?.phone &&
                              touched.subscriber?.phone && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.subscriber.phone}
                                  defaultlabel="Phone Number is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={'groupNumber'}
                              labelid="groupNumberText"
                              defaultlabelid="Group Number"
                              handlechange={(value: string) => {
                                const validatedValue = nameValidation(value);
                                if (validatedValue !== undefined) {
                                  setFieldValue(
                                    'subscriber.subscriberGroupNumber',
                                    validatedValue,
                                  );
                                }
                              }}
                              Value={
                                values.subscriber.subscriberGroupNumber ?? ''
                              }
                              inputProps={{
                                maxLength: 40,
                              }}
                            />
                            {errors.subscriber?.subscriberGroupNumber &&
                              touched.subscriber?.subscriberGroupNumber && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={
                                    errors.subscriber.subscriberGroupNumber
                                  }
                                  defaultlabel="Group Number cannot exceed 40 characters"
                                />
                              )}
                          </Grid>

                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              name={'subscriberPlan'}
                              labelid="subscriberPlanText"
                              defaultlabelid="Plan"
                              handlechange={(value: string) => {
                                const validatedValue = nameValidation(value);
                                if (validatedValue !== undefined) {
                                  setFieldValue(
                                    'subscriber.subscriberPlan',
                                    validatedValue,
                                  );
                                }
                              }}
                              Value={values.subscriber.subscriberPlan ?? ''}
                              inputProps={{
                                maxLength: 40,
                              }}
                            />
                            {errors.subscriber?.subscriberPlan &&
                              touched.subscriber?.subscriberPlan && (
                                <MediumTypography
                                  sxProps={{ color: 'red' }}
                                  labelid={errors.subscriber.subscriberPlan}
                                  defaultlabel="Subscriber Plan cannot exceed 40 characters"
                                />
                              )}
                          </Grid>

                          <Grid container item xs={9}>
                            <AddressForm
                              address1PlaceholderId="address1Text"
                              address2PlaceholderId="address2Text"
                              zipCodePlaceholderId="Clientpage.Zip"
                              cityPlaceholderId="Clientpage.City"
                              statePlaceholderId="Clientpage.State"
                              value={{
                                address1: values.subscriber.address1 ?? '',
                                address2: values.subscriber.address2 ?? '',
                                city: values.subscriber.city ?? {
                                  id: 'asd',
                                  label: '',
                                },
                                zipCode: values.subscriber.zipCode ?? '',
                                state: values.subscriber.state ?? '',
                              }}
                              isClient={true}
                              disableForm={false}
                              formRequired={true}
                              formikErrors={{
                                address1: errors.subscriber?.address1,
                                address2: errors.subscriber?.address2,
                                city:
                                  errors.subscriber?.city &&
                                  typeof errors.subscriber?.city === 'object'
                                    ? (errors.subscriber?.city as OptionType)
                                        .label
                                    : undefined,
                                zipCode: errors.subscriber?.zipCode,
                                state: errors.subscriber?.state,
                              }}
                              formikTouched={{
                                address1: touched.subscriber?.address1,
                                address2: touched.subscriber?.address2,
                                zipCode: touched.subscriber?.zipCode,
                                city: touched.subscriber?.city,
                                state: touched.subscriber?.state,
                              }}
                              handleAddressUpdate={(newAddress) => {
                                formRef.current?.setValues({
                                  ...values,
                                  subscriber: {
                                    ...values.subscriber,
                                    address1: newAddress.address1,
                                    address2: newAddress.address2,
                                    zipCode: newAddress.zipCode,
                                    city: newAddress.city,
                                    state: newAddress.state,
                                  },
                                });
                              }}
                            />
                          </Grid>
                          <Grid container item xs={9} />
                        </Grid>
                      </Box>
                    </>
                  )}
                </Formik>
              </Card>
            </Box>
          </Box>
        </Box>

        <Box>
          <Grid
            item
            md={6}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '16px',
            }}
            visibility={hasEditPermission ? 'visible' : 'hidden'}
          >
            <Grid item>
              <ButtonComponent
                className="btn-primary btn-cancel"
                variantType="contained"
                labelId="Insurance.cancel"
                defaultLabelId="Cancel"
                onClick={() => {
                  if (!formRef.current) return;

                  const initialValues = formRef.current.initialValues;
                  const values = formRef.current.values;

                  if (_.isEqual(initialValues, values)) {
                    clearForm();
                  } else {
                    setOpenModal(true);
                  }
                }}
              />
            </Grid>
            <Grid item>
              <ButtonComponent
                className="btn-primary btn-submit ml-md"
                variantType="contained"
                type="submit"
                labelId={`${
                  action === 'ADD'
                    ? 'Insurance.Addinsurance'
                    : 'Insurance.Updateinsurance'
                }`}
                defaultLabelId={`${
                  action === 'ADD' ? 'Add Insurance' : 'Update Insurance'
                }`}
                onClick={() => {
                  if (!formRef.current) return;

                  const currentValues = structuredClone(formRef.current.values);
                  const initialValues = formRef.current.initialValues;

                  if (_.isEqual(initialValues, currentValues)) {
                    showToastr(
                      'error',
                      'insuranceNoDataMessage',
                      'No details have been entered in the insurance to save.',
                    );
                    return;
                  }
                  formRef.current.handleSubmit();
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientInsurance;
